import { Injectable } from '@angular/core';
import { Role, User } from '@core/model/user.model';
import { FlamingoHttpService } from '@flamingo/service/flamingo-http.service';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { filter, first, map, tap } from 'rxjs/operators';
import { NamespaceModel } from '@x/model/namespace.model';
import { GlobalNamespaceService } from '@x/service/global-namespace.service';
import { LoggedUser, Permissions } from '../model/logged-user.model';

@Injectable({
  providedIn: 'root',
})
export class LoggedUserService {
  private _user$: BehaviorSubject<LoggedUser | null> = new BehaviorSubject<LoggedUser | null>(null);
  public user$: Observable<LoggedUser> = this._user$.asObservable()
    .pipe(
      filter((user: LoggedUser | null): user is LoggedUser => !!user),
    );

  private _namespaces$: BehaviorSubject<NamespaceModel[]> = new BehaviorSubject<NamespaceModel[]>([]);
  public namespaces$: Observable<NamespaceModel[]> = this._namespaces$.asObservable();

  constructor(
    private flamingoHttpService: FlamingoHttpService,
    private namespaceService: GlobalNamespaceService,
  ) {
  }

  get user(): LoggedUser | null {
    return this._user$.value;
  }

  get namespaces(): NamespaceModel[] {
    return this._namespaces$.value;
  }

  public fetch(): Observable<LoggedUser> {
    return this.namespaceService.fetch()
      .pipe(
        switchMap(namespaces => this.flamingoHttpService.get<User>(`/me`)
          .pipe(
            // Set namespaces
            tap(user => this._namespaces$.next(namespaces
              // hide disabled namespaces for everyone
              .filter(n => !!n.enabled)
              // .filter(n => user.role === Role.ADMIN ? true : n.enabled)
              // hide not assigned namespaces
              .filter(n => user.role === Role.ADMIN ? true : user.namespaces.includes(n.namespace)),
            )),
          )),
        first(),
        map(user => ({ ...user, role: { name: user.role, permissions: [] } } as LoggedUser)),
        // Set base permission
        map(user => {
          switch (user.role.name) {
            case Role.ADMIN:
              user.role.permissions.push(
                { name: Permissions.canManageDocuments },
                { name: Permissions.canUnlockDocuments },
                { name: Permissions.canCompileDocuments },
                { name: Permissions.canManageUsers },
                { name: Permissions.canManageCompanies },
                { name: Permissions.canManagePipelines },
                { name: Permissions.canManageJobs },
                { name: Permissions.canManageSelf },
                { name: Permissions.canDowngradeRole },
                { name: Permissions.canSeeHiddenPipelines },
                { name: Permissions.canManageNamespaces },
                { name: Permissions.canManageTags },
                { name: Permissions.canUseAiAssistant },
                { name: Permissions.canUseAiGeneration },
                { name: Permissions.canUploadFiles },
              );
              break;
            case Role.OPERATOR:
              user.role.permissions.push(
                { name: Permissions.canUnlockDocuments },
                { name: Permissions.canLockDocuments },
                { name: Permissions.canManageOwnDocuments },
                { name: Permissions.canCompileDocuments },
                { name: Permissions.canManageCompanies },
                { name: Permissions.canManageSelf },
                { name: Permissions.canUploadFiles },
                { name: Permissions.canManageTags },
              );
              break;
            case Role.USER:
              user.role.permissions.push(
                { name: Permissions.canManageSelf },
                // { name: Permissions.canSeeHiddenPipelines },
                { name: Permissions.canUseAiAssistant },
                { name: Permissions.canUploadFiles },
              );
              break;
            default:
              user.role.permissions.push(
                { name: Permissions.canManageSelf },
              );
              break;
          }
          console.log(user.role.name, user.originalRole);
          if (user.role.name !== user.originalRole) {
            user.role.permissions.push(
              { name: Permissions.canRestoreRole },
            );
          }
          return user;
        }),
        tap((user) => this._user$.next(user)),
      );
  }

  public clear(): void {
    this._user$.next(null);
  }

  can(permission: Permissions | Permissions[]): boolean {
    if (!Array.isArray(permission)) {
      permission = [permission];
    }
    return permission.every(p => this.user?.role?.permissions.map(v => v.name).includes(p));
  }

  canAtLeastOne(permission: Permissions | Permissions[]): boolean {
    if (!Array.isArray(permission)) {
      permission = [permission];
    }
    return permission.some(p => this.user?.role?.permissions.map(v => v.name).includes(p));
  }

  cannot(permission: Permissions | Permissions[]): boolean {
    if (!Array.isArray(permission)) {
      permission = [permission];
    }
    return !permission.every(p => this.user?.role?.permissions.map(v => v.name).includes(p));
  }

}
